import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Order, OrderFilter } from '../../types';
import { apiService } from '../../services/apiService';
import { showSuccessMessage, showErrorMessage } from './uiSlice';

interface OrderState {
  newOrders: Order[];
  orderHistory: Order[];
  selectedOrder: Order | null;
  loading: boolean;
  error: string | null;
  filter: OrderFilter;
  processingOrders: string[]; // Track which orders are being processed
  pollingEnabled: boolean;
}

const initialState: OrderState = {
  newOrders: [],
  orderHistory: [],
  selectedOrder: null,
  loading: false,
  error: null,
  filter: {},
  processingOrders: [],
  pollingEnabled: false,
};

// Async thunks

/**
 * Fetch new orders. If branchId is provided, returns orders for that branch only.
 * If branchId is not provided, returns orders for all branches.
 */
export const fetchNewOrders = createAsyncThunk(
  'orders/fetchNewOrders',
  async (params: { branchId?: string } = {}, { rejectWithValue, dispatch }) => {
    try {
      const orders = await apiService.getNewOrders(params.branchId);
      // Removed success notification for load operations to reduce noise
      return orders;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch new orders';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);



export const fetchOrderHistory = createAsyncThunk(
  'orders/fetchHistory',
  async ({ branchId, dateFrom, dateTo }: { branchId: string | null; dateFrom?: string; dateTo?: string }, { rejectWithValue, dispatch }) => {
    try {
      const orders = await apiService.getOrderHistory(branchId, dateFrom, dateTo);
      // Removed success notification for load operations to reduce noise
      return orders;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch order history';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchById',
  async (orderId: string, { rejectWithValue, dispatch }) => {
    try {
      const order = await apiService.getOrderById(orderId);
      // Removed success notification for load operations to reduce noise
      return order;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch order details';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const retryFailedOrder = createAsyncThunk(
  'orders/retry',
  async (orderId: string, { rejectWithValue, dispatch }) => {
    try {
      const result = await apiService.retryFailedOrder(orderId);
      // Use the message from the server response if available
      const successMessage = result.message || `Successfully retried order #${result.order.cloudShortCode}`;
      dispatch(showSuccessMessage(successMessage));
      return { message: result.message, order: result.order };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to retry order';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setSelectedOrder: (state, action: PayloadAction<Order | null>) => {
      state.selectedOrder = action.payload;
    },
    setFilter: (state, action: PayloadAction<OrderFilter>) => {
      state.filter = action.payload;
    },
    clearFilter: (state) => {
      state.filter = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    addProcessingOrder: (state, action: PayloadAction<string>) => {
      if (!state.processingOrders.includes(action.payload)) {
        state.processingOrders.push(action.payload);
      }
    },
    removeProcessingOrder: (state, action: PayloadAction<string>) => {
      state.processingOrders = state.processingOrders.filter(id => id !== action.payload);
    },
    setPollingEnabled: (state, action: PayloadAction<boolean>) => {
      state.pollingEnabled = action.payload;
    },
    updateOrderInLists: (state, action: PayloadAction<Order>) => {
      const order = action.payload;
      
      // Update in all relevant lists
      const updateInList = (list: Order[]) => {
        const index = list.findIndex(o => o.id === order.id);
        if (index !== -1) {
          list[index] = order;
        }
      };
      
      updateInList(state.newOrders);
      updateInList(state.orderHistory);
      
      if (state.selectedOrder?.id === order.id) {
        state.selectedOrder = order;
      }
    },
    removeOrderFromNewOrders: (state, action: PayloadAction<string>) => {
      state.newOrders = state.newOrders.filter(o => o.id !== action.payload);
    },
  },  extraReducers: (builder) => {
    builder
      // Fetch new orders
      .addCase(fetchNewOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNewOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.newOrders = action.payload;
      })
      .addCase(fetchNewOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch order history
      .addCase(fetchOrderHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.orderHistory = action.payload;
      })
      .addCase(fetchOrderHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch order by ID
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
        // Update in lists if exists
        const updateInList = (list: Order[]) => {
          const index = list.findIndex(o => o.id === action.payload.id);
          if (index !== -1) {
            list[index] = action.payload;
          }
        };
        updateInList(state.newOrders);
        updateInList(state.orderHistory);
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Retry failed order
      .addCase(retryFailedOrder.pending, (state, action) => {
        if (!state.processingOrders.includes(action.meta.arg)) {
          state.processingOrders.push(action.meta.arg);
        }
      })
      .addCase(retryFailedOrder.fulfilled, (state, action) => {
        state.processingOrders = state.processingOrders.filter(id => id !== action.payload.order.id);
        // Update order in all lists
        const updateInList = (list: Order[]) => {
          const index = list.findIndex(o => o.id === action.payload.order.id);
          if (index !== -1) {
            list[index] = action.payload.order;
          }
        };
        updateInList(state.newOrders);
        updateInList(state.orderHistory);
        
        if (state.selectedOrder?.id === action.payload.order.id) {
          state.selectedOrder = action.payload.order;
        }
      })
      .addCase(retryFailedOrder.rejected, (state, action) => {
        state.processingOrders = state.processingOrders.filter(id => id !== action.meta.arg);
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedOrder,
  setFilter,
  clearFilter,
  clearError,
  addProcessingOrder,
  removeProcessingOrder,
  setPollingEnabled,
  updateOrderInLists,
  removeOrderFromNewOrders,
} = orderSlice.actions;

export default orderSlice.reducer;
